import { Link, useParams } from 'react-router-dom';
import { useWorld } from '../../hooks/useChancellery';
import { COLORS } from '../../constants/theme';

/** Chancellery world detail — route: /chancellery/universes/:universeId/worlds/:worldId */
export function WorldDetail() {
  const { universeId, worldId } = useParams<{ universeId: string; worldId: string }>();
  const { data: world } = useWorld(universeId, worldId);

  if (!world) {
    return (
      <div>
        <Link to="/chancellery" className="text-sm" style={{ color: COLORS.link }}>
          ← Universes
        </Link>
        <p className="mt-4 text-sm" style={{ color: COLORS.text.tertiary }}>
          World not found.
        </p>
      </div>
    );
  }

  return (
    <div>
      <nav className="text-sm mb-3" aria-label="Breadcrumb">
        <Link to="/chancellery" style={{ color: COLORS.link }}>Universes</Link>
        <span style={{ color: COLORS.text.tertiary }}> / </span>
        <Link to={`/chancellery/universes/${world.universeId}`} style={{ color: COLORS.link }}>
          {world.universeName}
        </Link>
        <span style={{ color: COLORS.text.tertiary }}> / </span>
        <span style={{ color: COLORS.text.secondary }}>
          Div {world.divRank} — {world.name}
        </span>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold mb-1" style={{ color: COLORS.text.primary }}>
          {world.name}
        </h1>
        <p className="text-sm" style={{ color: COLORS.text.tertiary }}>
          <span
            className="px-2 py-0.5 rounded text-xs font-semibold mr-2"
            style={{ backgroundColor: 'rgba(106,158,142,0.15)', color: COLORS.link }}
          >
            Div {world.divRank}
          </span>
          of {world.universeName} — the top rung of the ladder.
        </p>
      </div>

      <div className="text-xs uppercase tracking-wide mb-1" style={{ color: COLORS.text.tertiary }}>
        Size
      </div>
      <p className="text-sm mb-4 max-w-[60ch]" style={{ color: COLORS.text.tertiary }}>
        Max values come from {world.universeName}'s properties for the current age — read-only here,
        same for every tier. "Filled" is what {world.name} is actually using right now.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {world.size.map((stat) => {
          const pct = Math.round((stat.current / stat.max) * 100);
          return (
            <div key={stat.label} className="rounded p-4" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
              <div className="text-xs mb-1" style={{ color: COLORS.text.tertiary }}>{stat.label}</div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-xl font-semibold" style={{ color: COLORS.text.primary }}>{stat.current}</span>
                <span className="text-xs" style={{ color: COLORS.text.tertiary }}>of {stat.max}</span>
              </div>
              <div
                role="progressbar"
                aria-valuenow={stat.current}
                aria-valuemin={0}
                aria-valuemax={stat.max}
                aria-label={`${stat.label} utilization`}
                className="h-1.5 rounded-full overflow-hidden mb-1"
                style={{ backgroundColor: COLORS.cardAlt }}
              >
                <div className="h-full" style={{ width: `${pct}%`, backgroundColor: COLORS.link }} />
              </div>
              <div className="text-xs" style={{ color: COLORS.text.tertiary }}>{stat.note}</div>
            </div>
          );
        })}
      </div>

      <div className="text-xs uppercase tracking-wide mb-2" style={{ color: COLORS.text.tertiary }}>
        Age history
      </div>
      <div className="flex flex-col gap-2">
        {world.ageHistory.map((entry) => (
          <div
            key={entry.ageNumber}
            className="rounded p-4"
            style={{
              backgroundColor: COLORS.card,
              border: `1px ${entry.isGenesis ? 'dashed' : 'solid'} ${COLORS.border}`,
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium" style={{ color: COLORS.text.primary }}>
                Age {entry.ageNumber} — {entry.name}
              </span>
              {entry.isCurrent && (
                <span
                  className="px-2 py-0.5 rounded text-xs font-semibold"
                  style={{ backgroundColor: '#0f2a1a', color: COLORS.positive }}
                >
                  Active
                </span>
              )}
            </div>
            <div className="text-xs mb-1" style={{ color: COLORS.text.tertiary }}>{entry.dateRangeLabel}</div>
            <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
              {entry.isGenesis ? (
                `${world.universeName} founded here — no prior tier to swap with`
              ) : (
                <>
                  <span style={{ fontFamily: 'monospace', color: COLORS.text.secondary }}>{entry.promotedIn}</span> promoted
                  in from Div {world.divRank + 1} ·{' '}
                  <span style={{ fontFamily: 'monospace', color: COLORS.text.secondary }}>{entry.relegatedOut}</span> relegated
                  to Div {world.divRank + 1}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
